import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getParameterByName } from '@libraries/helpers';
import { filter, map, startWith } from 'rxjs/operators';
import { IsLocalMatcherService } from './is-local-matcher.service';

export enum LogLevel {
  ALL = 1,
  INFO,
  WARN,
  ERROR,
  NONE,
}
export type LogLevelNames = keyof typeof LogLevel;
type consoleMethods = 'log' | 'info' | 'warn' | 'error';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  /** PUBLIC  PROPERTIES*/
  public logLevel: LogLevel;

  /** INIT LOGIC */
  constructor(
    private route: ActivatedRoute,
    private host: IsLocalMatcherService
  ) {
    this.logLevel = LogLevel[this.host.isLocal ? 'ALL' : 'NONE'];
    this.route.queryParams
      .pipe(
        // need to manually get the first parameter from the url
        // because of this issue: https://github.com/angular/angular/issues/12157
        // otherwise the logLevel is incorrect until the correct queryParams are emitted
        startWith({ logLevel: getParameterByName('logLevel') }),
        map(({ logLevel }) => `${logLevel}`.toUpperCase() as LogLevelNames),
        filter(this.isValidLevel)
      )
      .subscribe((name: LogLevelNames) => (this.logLevel = LogLevel[name]));
  }

  /** PRIVATE METHODS */
  private logIt(
    levelName: LogLevelNames,
    method: consoleMethods,
    ...args: any[]
  ) {
    const allowed = LogLevel[levelName] >= this.logLevel;
    if (allowed) console[method](...args);
  }

  private logGroup(name: string, lines: any[][], collapsed: boolean) {
    if (this.logLevel <= LogLevel.INFO) {
      (<any>console)[`group${collapsed ? 'Collapsed' : ''}`](name);
      lines.forEach((args) => console.log(...args));
      console.groupEnd();
    }
  }

  private isValidLevel = (lvl: LogLevelNames) =>
    LogLevel[lvl] && /[1-5]/.exec(lvl) === null;

  /** PUBLIC METHODS */
  public error = (...args: any[]) => this.logIt('ERROR', 'error', ...args);

  public warn = (...args: any[]) => this.logIt('WARN', 'warn', ...args);

  public info = (...args: any[]) => this.logIt('INFO', 'info', ...args);

  public log = (...args: any[]) => this.logIt('ALL', 'log', ...args);

  public group = (name: string, lines: Array<any[]>) =>
    this.logGroup(name, lines, false);

  public groupCollapsed = (name: string, lines: Array<any[]>) =>
    this.logGroup(name, lines, true);
}
